import {useState} from 'react';
import {ChevronRight, Trash2, RotateCcw} from 'lucide-react';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {type DeliveryAction} from '@/api/delivery-actions';
import {toneBadge} from '@/lib/status';

// Renders every outbound delivery action captured for one endpoint - the request always, and a
// response once one arrives: the real one for a live endpoint, or the endpoint's simulated status
// code for one in test mode (where the request never actually left). Shared by HopperCard (per-hopper
// webhook/sheet) and P0SlackAlertConfigCard (the singleton Slack webhook), which both fetch this same
// shape.
//
// The panel sits inside a Card, so it is one bordered surface on the page background with the rows
// divided by rules rather than three nested tinted boxes (panel, row, pre), which read as one grey
// smear in the 3.0 palette.
export function DeliveryActionsPanel({
  actions,
  isFetching,
  onClear,
  clearing,
  onRetry,
  retrying
}: {
  readonly actions: DeliveryAction[] | undefined;
  readonly isFetching: boolean;
  readonly onClear: () => void;
  readonly clearing: boolean;
  readonly onRetry: (action: DeliveryAction) => void;
  readonly retrying: boolean;
}) {
  return (
    <div className="border-border bg-card mt-3 rounded-lg border">
      <div className="flex items-center justify-between px-3 py-2">
        <h4 className="text-foreground text-xs font-medium">
          Delivery actions {actions ? `(${actions.length})` : null}
        </h4>
        <Button
          type="button"
          variant="outline"
          size="xs"
          disabled={clearing || !actions?.length}
          className="text-muted-foreground hover:text-destructive-ink"
          onClick={onClear}>
          <Trash2 aria-hidden /> Clear
        </Button>
      </div>
      {isFetching && !actions ? (
        <p className="text-muted-foreground border-border flex items-center gap-2 border-t px-3 py-2 text-xs">
          <Spinner aria-label="Loading actions" /> Loading actions…
        </p>
      ) : null}
      {actions?.length === 0 ? (
        <p className="text-muted-foreground border-border border-t px-3 py-2 text-xs">
          No deliveries yet. Fire this endpoint to see requests appear here.
        </p>
      ) : null}
      {actions?.length ? (
        <div className="divide-border border-border divide-y border-t">
          {actions.map((action) => (
            <ActionRow key={action.id} action={action} retrying={retrying} onRetry={onRetry} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ActionRow({
  action,
  onRetry,
  retrying
}: {
  readonly action: DeliveryAction;
  readonly onRetry: (action: DeliveryAction) => void;
  readonly retrying: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const canRetry = action.outcome !== null && action.outcome !== 'succeeded';

  // The trigger stays in the actions row rather than on its own line, so the row keeps one
  // trailing control group; Collapsible only needs both halves under one root.
  return (
    <Collapsible open={expanded} className="block px-3 py-2.5" onOpenChange={setExpanded}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-foreground font-mono text-xs font-medium">
          {action.method ? `${action.method} ` : ''}
          {action.target_type}
        </span>
        <div className="flex items-center gap-2">
          <OutcomeBadge action={action} />
          <span className="text-muted-foreground text-xs">
            {new Date(action.created_at).toLocaleString()}
          </span>
          {canRetry ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    disabled={retrying}
                    className="text-muted-foreground"
                    onClick={() => {
                      onRetry(action);
                    }}
                  />
                }>
                <RotateCcw aria-hidden /> Retry
              </TooltipTrigger>
              <TooltipContent>Retry this delivery</TooltipContent>
            </Tooltip>
          ) : null}
          <CollapsibleTrigger
            render={
              <Button type="button" variant="ghost" size="xs" className="text-muted-foreground" />
            }>
            <ChevronRight
              aria-hidden
              className="size-3 transition-transform in-data-panel-open:rotate-90"
            />
            {expanded ? 'Less' : 'More details'}
          </CollapsibleTrigger>
        </div>
      </div>
      <CollapsibleContent>
        <div className="mt-2 space-y-2">
          {action.detail ? (
            <div>
              <p className="text-muted-foreground text-xs font-medium">Response</p>
              <pre className="border-border bg-card mt-1 overflow-x-auto rounded-md border p-2 font-mono text-xs whitespace-pre-wrap">
                {action.detail}
              </pre>
            </div>
          ) : null}
          {action.headers && Object.keys(action.headers).length > 0 ? (
            <div>
              <p className="text-muted-foreground text-xs font-medium">Headers</p>
              <pre className="border-border bg-card mt-1 overflow-x-auto rounded-md border p-2 font-mono text-xs whitespace-pre-wrap">
                {JSON.stringify(action.headers, null, 2)}
              </pre>
            </div>
          ) : null}
          <div>
            <p className="text-muted-foreground text-xs font-medium">Body</p>
            <pre className="border-border bg-card mt-1 overflow-x-auto rounded-md border p-2 font-mono text-xs whitespace-pre-wrap">
              {formatBody(action.body)}
            </pre>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// The label carries the meaning on its own (PENDING / status code / outcome word), so the variant
// only reinforces it: outline for not-yet, and the status colours for success and failure.
function OutcomeBadge({action}: {readonly action: DeliveryAction}) {
  if (action.outcome === null) {
    return <Badge variant="outline">PENDING</Badge>;
  }

  const succeeded = action.outcome === 'succeeded';
  return (
    <Badge {...toneBadge(succeeded ? 'success' : 'destructive')}>
      {action.status_code ?? action.outcome}
    </Badge>
  );
}

function formatBody(body: string): string {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}
